import { ua, eventEmitter } from '@weapp/utils';
import { IconMapType, LoadParamsType, LoadOptionsType } from '../types';
import { IconNames } from '../types';
import { AnyObj } from '../../../types/common';
import { constants } from '../../../lib';

const { uiAppName } = constants;

/** iconMap 缓存记录 */
let iconMap: IconMapType | null = null;
/** iconMap 小写图标名称映射 */
let iconLowerKeysMap: { [key: string]: string } | null = null;



let appendSymbolMap: { [key: string]: 'loading' | 'success' | 'failed' } = {}

export const svgElement = `  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    id="_WEAPP_UI_ICON_SVG_REFS_NODE_"
    style="position:absolute;width:0;height:0"
  >
    <defs>
    </defs>
  </svg>`;

/** icon svg symbol 挂载位置 */
let symbolArea: SVGDefsElement | HTMLElement | null | undefined;

let listState: AnyObj = {};

const getIconLoader = () => import(
  /* webpackChunkName: 'icon' */
  './icon-loader'
);
/**
 * 加载 script 文件获取 json 内容
 * @param params LoadParamsType
 * @param options LoadOptionsType
 * @returns {Promise<any>}
 */
const loadJSONJS = (params: LoadParamsType, options?: Partial<LoadOptionsType>): Promise<any> => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.loadJSONJS?.(params, options));
}

/**
 * 获取 js chunk 文件内容
 * @param bundleId string
 * @param path string
 * @param domain string | undefined
 * @returns Promise
 */
// export function getJson(params: LoadParamsType, options?: Partial<LoadOptionsType>): Promise<any> {
//   const taskPromise = loadJSONJS(params, options);
//   return Promise.race([taskPromise,
//     new Promise(function (resolve, reject) {
//       setTimeout(() => {
//         reject(`Load ${params.path} timed out after ${options?.timeout || 3000}`);
//       }, options?.timeout || 3000);
//     })]);
// }

/**
 * Gets the icon resource domain.
 * @returns {Promise<string>} The promise of the icon resource domain.
 */
const getIconResourceDomain = async (domain?: string) => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.getIconResourceDomain?.(domain));
}

/**
 * Loads the icon map from the resource domain.
 * @returns {Promise} A promise that resolves to the icon map.
 */
const loadIconMap = async (params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>): Promise<IconMapType> => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.loadIconMap?.(params, options));
}

/**
 * 获取图标库对应图标所在的 chunk 文件内容
 * @param iconName string
 * @param params LoadParamsType
 * @param options LoadOptionsType
 * @returns {Promise<string>} A promise that resolves to the icon chunk
 */
const getIconChunk = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>): Promise<string> => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.getIconChunk?.(iconName, params, options));
}

export const eventEmit = (iconName: string, res: boolean) => {
  listState[iconName] = res;
  eventEmitter.emit(uiAppName, iconName, res);
}

const loadIconChunk = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>) => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.loadIconChunk?.(iconName, params, options));
}

/**
 * 获取图标库图片图标的 src
 * @param iconName string
 * @param params LoadParamsType
 * @param options LoadOptionsType
 * @returns {Promise<string>} A promise that resolves to the image src
 */
const loadImageIcon = async (iconName: string, params?: Partial<LoadParamsType>, options?: Partial<LoadOptionsType>) => {
  return getIconLoader()?.then((defaultModule) => defaultModule?.loadImageIcon?.(iconName, params, options));
}

/** 
 * 返回图标库的 IconMap 问题
 * 更推荐使用 loadIconMap，仅当明确系统已经加载过 IconMap 时才可使用该方法
 */
export const getIconMap = () => {
  return iconMap || {};
}

export const setIconMap = (_iconMap: IconMapType) => {
  iconMap = _iconMap;
}

export const isIconMapLoaded = () => {
  return iconMap !== null;
}

export const getIconLowerKeysMap = () => {
  return iconLowerKeysMap || {};
}

export const setIconLowerKeysMap = (_iconLowerKeysMap: { [key: string]: string } | null) => {
  iconLowerKeysMap = _iconLowerKeysMap;
}

export const isIconLowerKeysMapLoaded = () => {
  return iconLowerKeysMap !== null;
}

export const getListState = () => listState;
export const setListState = (_listState: AnyObj) => listState = _listState;

export const getSymbolArea = () => symbolArea;
export const setSymbolArea = (_symbolArea: SVGDefsElement | HTMLElement | null | undefined) => symbolArea = _symbolArea;

const checkIsIconLoaded = (iconName: string) => {
  return appendSymbolMap[iconName] === 'success';
}

export const appendSymbolToRefs = (iconName: string, svgContent: string, options?: {
  onError?: () => void;
  onSuccess?: () => void;
}) => {
  if (!(iconName in appendSymbolMap) || appendSymbolMap[iconName] === 'loading') {
    eventEmitter.once(uiAppName, 'appendSymbolToRefs_' + iconName, (res: boolean) => {
      if (res) {
        options?.onSuccess?.();
      } else {
        options?.onError?.();
      }
    });
  } else {
    if (appendSymbolMap[iconName] === 'success') {
      options?.onSuccess?.();
    } else if (appendSymbolMap[iconName] === 'failed') {
      options?.onError?.();
    }
  }

  if (!(iconName in appendSymbolMap)) {
    appendSymbolMap[iconName] = 'loading';
    if (typeof symbolArea === 'undefined') {
      symbolArea = null;
      document.body.insertAdjacentHTML('afterbegin', svgElement);
      symbolArea = document.getElementById('_WEAPP_UI_ICON_SVG_REFS_NODE_')?.getElementsByTagName('defs')[0];
    }
    if (svgContent && symbolArea) {
      // 将 svg 节点替换为 symbol 节点
      const content = svgContent.replace('<svg', '').replace('svg>', '');
      let str = `<symbol id=${iconName} ${content}symbol>`;

      if (ua.browser === 'IE') {
        // // 创建一个临时的 <div> 元素，用于解析 SVG 内容
        // const tempDiv = document.createElement('div');
        // tempDiv.innerHTML = str;
        // // 获取 <symbol> 元素
        // const symbolElement = tempDiv.querySelector('symbol');
        // if (symbolElement) {
        //   // ie11 不支持 insertAdjacentHTML
        //   symbolArea.appendChild(symbolElement);
        // }

        // 挂载 symbol
        symbolArea.insertAdjacentHTML(
          'afterbegin',
          `<svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          id="_WEAPP_UI_ICON_SVG_REFS_NODE_${iconName?.toUpperCase()}"
          style="position:absolute;width:0;height:0"
        >
          <defs>
            ${str}
          </defs>
        </svg>`
        )
      } else {
        symbolArea.insertAdjacentHTML('afterbegin', str);
      }

      appendSymbolMap[iconName] = 'success';
      eventEmitter.emit(uiAppName, 'appendSymbolToRefs_' + iconName, true);
    } else {
      appendSymbolMap[iconName] = 'failed';
      eventEmitter.emit(uiAppName, 'appendSymbolToRefs_' + iconName, false);
    }
  }
}

/** 
 * 挂载 svg symbol
 * 配合 <svg><use xlink:href="#iconName"></use></svg> 使用，适用于无法使用 Icon 组件，但是又希望使用图标库图标的地方
 */
export const renderSvgSymbol = (iconName: IconNames) => {
  const isIconLoaded = checkIsIconLoaded(iconName);
  if (isIconLoaded) return;
  import(
    /* webpackChunkName: 'icon' */
    './getSvgIconDom'
  ).then((defaultModule: any) => {
    return defaultModule?.getSvgIconStr?.(iconName).then((res: { svgStr: any; }) => {
      const svgStr = res.svgStr;
      appendSymbolToRefs(iconName, svgStr);
    })
  })
};


const iconLoader = {
  getIconResourceDomain,
  getJson: loadJSONJS,

  loadIconMap,
  getIconMap,
  getIconLowerKeysMap,
  loadIconChunk,
  getIconChunk,
  loadImageIcon,

  renderSvgSymbol,
  checkIsIconLoaded,
  appendSymbolToRefs
};

export type IconLoaderType = typeof iconLoader;

export default iconLoader;